import { FC, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Box, Card, TableContainer, Typography } from '@mui/material'

import { FtueVideo } from '../../../api/core'
import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import { GRTable, GRTableColumn } from '../../../components/GRTable/GRTable'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import { ConfirmDialogData } from '../../../types/ConfirmDialogData'
import { useDeleteOnboardingVideoMutation, useGetOnboardingVideosQuery } from '../../api/onboarding-videos'
import { InternalListToolbar } from '../../components/InternalListToolbar/InternalListToolbar'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { rowActionsColumn } from '../../components/RowActions/RowActions'
import { getListThumbnail, sortOnboardingVideos } from '../../features/onboarding-videos/helpers/onboardingVideoHelpers'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

type CustomProps = { onEdit: (video: FtueVideo) => void; onDelete: (video: FtueVideo) => void }

export const onboardingVideosPath = '/internal/onboarding-videos'
export const onboardingVideoAddPath = `${onboardingVideosPath}/add`
export const onboardingVideoEditPath = (id: string) => `${onboardingVideosPath}/${id}/edit`

export const OnboardingVideosPage: FC = () => {
  usePage(pageService.getInternalPageWithId('internal-onboarding-videos'))
  const { t } = useTranslation()
  const { notifySuccess, notifyError } = useMutationFeedback()
  const navigate = useNavigate()
  const videosQuery = useGetOnboardingVideosQuery()
  const [deleteVideo] = useDeleteOnboardingVideoMutation()
  const [confirmDialogData, setConfirmDialogData] = useState<ConfirmDialogData<FtueVideo>>()

  const rows = useMemo(() => sortOnboardingVideos(videosQuery.data ?? []), [videosQuery.data])

  const tableColumns: GRTableColumn<FtueVideo, CustomProps>[] = useMemo(
    () => [
      {
        labelAccessor: t('internal-csm:onboarding_videos_thumbnail'),
        accessor: ({ row }) => {
          const thumbnail = getListThumbnail(row)
          return thumbnail ? (
            <Box component="img" src={thumbnail.link} alt="" sx={{ display: 'block', width: (theme) => theme.spacing(18) }} />
          ) : (
            <Typography color="text.secondary">---</Typography>
          )
        },
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 170, minWidth: 170 } },
      },
      {
        labelAccessor: t('internal-csm:onboarding_videos_title'),
        accessor: ({ row }) => <Typography fontWeight="bold">{row.names?.en || '---'}</Typography>,
        sortable: true,
        sortAccessor: ({ row }) => row.names?.en?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 240 } },
      },
      {
        labelAccessor: t('internal-csm:onboarding_videos_saas_link'),
        accessor: ({ row }) => row.videoName || '---',
        sortable: true,
        sortAccessor: ({ row }) => row.videoName?.toLowerCase() ?? '',
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { minWidth: 200 } },
      },
      {
        labelAccessor: t('internal-common:ftue_chapters'),
        accessor: ({ row }) => row.chapters?.length ?? 0,
        sortable: true,
        sortAccessor: ({ row }) => row.chapters?.length ?? 0,
        headerCellProps: { sx: { width: 110 } },
      },
      {
        labelAccessor: t('internal-csm:onboarding_videos_vimeo_id'),
        accessor: ({ row }) => row.vimeoId,
        cellProps: { align: 'left' },
        headerCellProps: { align: 'left', sx: { width: 130 } },
      },
      rowActionsColumn<FtueVideo, CustomProps>({
        actions: ({ row, customTableProps }) => [
          { type: 'edit', label: t('internal-csm:onboarding_videos_edit'), onClick: () => customTableProps?.onEdit(row) },
          { type: 'delete', label: t('internal-csm:onboarding_videos_delete'), onClick: () => customTableProps?.onDelete(row) },
        ],
      }),
    ],
    [t]
  )
  const [columns, setColumns] = useState(tableColumns)

  const customProps = useMemo<CustomProps>(
    () => ({
      onEdit: (video) => navigate(onboardingVideoEditPath(video.id)),
      onDelete: (video) =>
        setConfirmDialogData({
          title: t('internal-csm:onboarding_videos_delete'),
          confirmText: t('internal-csm:onboarding_videos_delete_confirm', { name: video.names?.en || '---' }),
          actionText: t('internal-common:delete'),
          destructiveAction: true,
          data: video,
        }),
    }),
    [navigate, t]
  )

  const { refetch } = videosQuery
  const handleConfirm = useCallback(
    async (confirmed?: ConfirmDialogData<FtueVideo>) => {
      setConfirmDialogData(undefined)
      const video = confirmed?.data
      if (!video) return
      const name = video.names?.en || '---'
      try {
        await deleteVideo(video.id).unwrap()
        const remaining = await refetch().unwrap()
        if (remaining.some((listed) => listed.id === video.id)) {
          notifyError(t('internal-csm:onboarding_videos_not_deleted', { name }))
          return
        }
        notifySuccess(t('internal-csm:onboarding_videos_deleted', { name }))
      } catch {
        notifyError(t('internal-csm:onboarding_videos_delete_failed', { name }))
      }
    },
    [deleteVideo, notifyError, notifySuccess, refetch, t]
  )

  return (
    <Box>
      <InternalListToolbar add={{ label: t('internal-csm:onboarding_videos_add'), onClick: () => navigate(onboardingVideoAddPath) }}>
        <Typography variant="body2" color="text.secondary">
          {t('internal-csm:onboarding_videos_intro')}
        </Typography>
        {videosQuery.data && (
          <Typography variant="body2" color="text.secondary">
            {t('internal-csm:onboarding_videos_count', { count: rows.length })}
          </Typography>
        )}
      </InternalListToolbar>

      <QueryStateBoundary isLoading={videosQuery.isLoading} isError={videosQuery.isError} errorText={t('internal-csm:onboarding_videos_load_failed')}>
        <TableContainer component={Card}>
          <GRTable
            rows={rows}
            columns={columns}
            onColumnsUpdated={setColumns}
            rowIdKey="id"
            customProps={customProps}
            noRowsLabel={t('internal-csm:onboarding_videos_none')}
            striped
            hoverable
          />
        </TableContainer>
      </QueryStateBoundary>

      <ConfirmDialog open={!!confirmDialogData} confirmDialogData={confirmDialogData} onClose={handleConfirm} />
    </Box>
  )
}
